import jsPDF from 'jspdf';
import logoMdm from '../../assets/images/logo.png';
import axiosInstance from '../../api/axiosInstance';
import Ciudades from '../../../src/assets/data/ciudades.json';
// @ts-ignore
import writtenNumber from 'written-number';

// Función auxiliar para obtener el nombre de la ciudad por código
function getNombreCiudad(codigoCiudad: string) {
  const ciudadObj = (Ciudades as any[]).find((c) => c.codigo === codigoCiudad);
  return ciudadObj ? ciudadObj.ciudad : codigoCiudad;
}

// Función auxiliar para obtener el valor de meta_data por key
function getMeta(meta_data: any[], key: string) {
  const found = meta_data.find((m) => m.key === key);
  return found ? found.value : 0;
}

function isTruthyFlag(value: any): boolean {
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value == null) return false;
  const normalized = String(value).trim().toLowerCase();
  return normalized === '1' || normalized === 'true' || normalized === 'yes';
}

/** meta_data puede venir como { key, value }[] o como objeto plano desde la API */
function metaDataAsKeyValueArray(raw: unknown): { key: string; value: unknown }[] {
  if (Array.isArray(raw)) {
    return raw.filter((m: any) => m && typeof m.key === 'string') as { key: string; value: unknown }[]
  }
  if (raw != null && typeof raw === 'object') {
    return Object.entries(raw as Record<string, unknown>).map(([key, value]) => ({ key, value }))
  }
  return []
}

// Función para convertir una imagen local a base64
function getImageBase64FromLocal(url: string): Promise<string> {
  return fetch(url)
    .then(res => res.blob())
    .then(blob => new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    }));
}

// Función para hacer wrap de texto según el ancho máximo
function wrapText(text: string, maxWidth: number, doc: jsPDF): string[] {
  if (!text) return [''];
  
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';
  
  words.forEach(word => {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const testWidth = doc.getTextWidth(testLine);
    
    if (testWidth > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  });
  
  if (currentLine) {
    lines.push(currentLine);
  }
  
  return lines.length > 0 ? lines : [''];
}

// Tipo de documento que se puede generar
type TipoDocumento = 'cotizacion' | 'comprobante';

export async function generarPDFDocumento(documentoId: number, tipo: TipoDocumento = 'cotizacion') {
  const token = localStorage.getItem('accessToken');
  
  // Determinar la ruta de la API según el tipo de documento
  const apiRoute = tipo === 'comprobante' ? `/list/${documentoId}` : `/cotizacion/${documentoId}`;
  
  // 1. Obtener detalle del documento
  const response = await axiosInstance.get(apiRoute, {
    headers: { Authorization: `Bearer ${token}` }
  });
  const documento = response.data;
  console.log(`Respuesta de la API ${apiRoute}:`, documento);
  
  // 2. Obtener logo en base64 desde archivo local
  const logoBase64 = await getImageBase64FromLocal(logoMdm);

  // 3. Extraer y mapear datos según el tipo de documento
  let meta, billing, agente, line_items, numeroDocumento, fechaExpedicion, vendedor, nombreCliente, nit, direccion, ciudad, telefono, fechaVencimiento = '';
  
  if (tipo === 'comprobante') {
    // Estructura para pedidos
    meta = documento.extraDetails || {};
    billing = documento.billingAddress || {};
    agente = documento.agente || {};
    line_items = documento.lineItems || [];
    numeroDocumento = documento.orders_id || documento.id || '';
    fechaExpedicion = documento.date_created ? documento.date_created.substring(0, 10) : '';
    vendedor = agente && agente.first_name && agente.last_name ? `${agente.first_name} ${agente.last_name}` : '';
    nombreCliente = documento.customer ? `${documento.customer.first_name || ''} ${documento.customer.last_name || ''}`.trim() : '';
    nit = documento.customer?.document || '';
    direccion = billing.address_1 || '';
    ciudad = getNombreCiudad(billing.city || '');
    telefono = documento.customer?.phone || billing.phone || '';
  } else {
    // Estructura para cotizaciones (mantener lógica original)
    meta = metaDataAsKeyValueArray(documento.meta_data);
    billing = documento.billing || {};
    agente = documento.agente || {};
    line_items = documento.line_items || [];
    
    numeroDocumento = getMeta(meta, 'cotizacion_id') || documento.id || '';
    fechaExpedicion = documento.date_created ? documento.date_created.substring(0, 10) : '';
    const expirationLimit = parseInt(getMeta(meta, 'expiration_limit')) || 0;
    if (fechaExpedicion && expirationLimit) {
      const fecha = new Date(fechaExpedicion);
      fecha.setDate(fecha.getDate() + expirationLimit);
      fechaVencimiento = fecha.toISOString().substring(0, 10);
    }
    vendedor = agente.first_name && agente.last_name ? `${agente.first_name} ${agente.last_name}` : '';
    nombreCliente = (billing.first_name || '') + (billing.last_name ? ' ' + billing.last_name : '');
    nit = getMeta(meta, 'documento');
    direccion = billing.address_1 || '';
    ciudad = getNombreCiudad(billing.city || '');
    telefono = getMeta(meta, 'phone') || documento.phone || '';
  }

  const metaPairsClienteNeto = metaDataAsKeyValueArray(documento.meta_data)
  const clienteNetoActivo = tipo === 'comprobante'
    ? (
      isTruthyFlag((meta as any)?.cliente_neto) ||
      isTruthyFlag(getMeta(metaPairsClienteNeto as any[], 'cliente_neto')) ||
      isTruthyFlag((documento as any).extraDetails?.cliente_neto)
    )
    : (
      isTruthyFlag(getMeta((meta || []) as any[], 'cliente_neto')) ||
      isTruthyFlag((documento as any).extraDetails?.cliente_neto)
    );
  
  console.log('Line items originales:', line_items);
  
  // Calcular total de descuentos de los productos
  const totalDescuentoItems = line_items.reduce((acc: number, item: any) => acc + (Number(item.discount) || 0), 0);

  // --- Consultar detalles de productos para combos ---
  for (let i = 0; i < line_items.length; i++) {
    const item = line_items[i];
    try {
      const prodResp = await axiosInstance.get(`/products/${item.product_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (prodResp.data) {
        console.log(`Respuesta de la API /products/${item.product_id}:`, prodResp.data);
        console.log(`  - tax_rate desde API: ${prodResp.data.tax_rate}`);
        console.log(`  - iva desde API: ${prodResp.data.iva}`);
        line_items[i].is_combo = prodResp.data.is_combo;
        line_items[i].components = prodResp.data.components || [];
        // Obtener tax_rate del producto si no está en line_items
        if (!line_items[i].tax_rate && prodResp.data.tax_rate !== undefined) {
          line_items[i].tax_rate = prodResp.data.tax_rate;
          console.log(`  - tax_rate actualizado en line_items: ${line_items[i].tax_rate}`);
        }
        if (prodResp.data.price !== undefined && prodResp.data.price !== null) {
          line_items[i].catalog_base_price = Number(prodResp.data.price);
        }
      }
    } catch (e) {
      console.error(`Error consultando /products/${item.product_id}:`, e);
      line_items[i].is_combo = false;
      line_items[i].components = [];
    }
  }

  // Debug: mostrar line_items después de procesar
  console.log('Line items después de procesar:', line_items.map((item: any) => ({
    name: item.name || item.product_name,
    tax_rate: item.tax_rate,
    price: item.price,
    quantity: item.quantity,
    is_combo: item.is_combo
  })));

  // Agregar envío como producto adicional si existe
  const costoEnvio = tipo === 'comprobante' 
    ? ((documento.envio) || documento.extraDetails?.costo_envio || documento.shipping_total || documento.shipping?.total || 0)
    : (getMeta(meta, 'costo_envio') || documento.shipping_total || documento.shipping?.total || 0);
  
  // Convertir a número y validar
  const costoEnvioNumero = Number(costoEnvio) || 0;
  
  // Función auxiliar para obtener el IVA de un item
  function getTaxRate(item: any): number {
    if (item.tax_rate !== undefined && item.tax_rate !== null && item.tax_rate !== '0.00') {
      const taxRateValue = Number(item.tax_rate);
      // Si el valor es menor a 1, asumimos que está en formato decimal (0.19 = 19%)
      // Si el valor es mayor o igual a 1, asumimos que está en formato porcentual (19 = 19%)
      return taxRateValue < 1 ? taxRateValue * 100 : taxRateValue;
    } else if (item.iva !== undefined && item.iva !== null) {
      return Number(item.iva);
    } else {
      return 19;
    }
  }

  function getUnitPriceForPdf(item: any): number {
    // Cliente neto: el precio guardado ya es el valor final (sin sumar IVA)
    return Number(item.price) || 0
  }

  function getLineSubtotalForPdf(item: any): number {
    const unit = getUnitPriceForPdf(item)
    const Q = Number(item.quantity) || 1
    const D = Number(item.discount) || 0
    return unit * Q - D
  }

  // Encontrar el IVA mayor solo para combos (entre sus componentes)
  let ivaMayor = 19; // IVA por defecto para envío
  
  // Solo calcular ivaMayor si hay combos
  const hasCombos = line_items.some((item: any) => 
    (item.is_combo === true || item.is_combo === 1) && 
    Array.isArray(item.components) && 
    item.components.length > 0
  );
  
  if (hasCombos) {
    line_items.forEach((item: any) => {
      // Solo revisar el IVA de los componentes si es un combo
      if ((item.is_combo === true || item.is_combo === 1) && Array.isArray(item.components) && item.components.length > 0) {
        item.components.forEach((component: any) => {
          const taxRateComponent = getTaxRate(component);
          if (taxRateComponent > ivaMayor) {
            ivaMayor = taxRateComponent;
          }
        });
      }
    });
  }
  
  // Calcular el costo de envío sin IVA usando el IVA mayor encontrado
  const factorIva = 1 + (ivaMayor / 100);
  const ivaEnvio = Math.round(costoEnvioNumero / factorIva);
  const valorIvaEnvio = Math.round(costoEnvioNumero - ivaEnvio);

  if (costoEnvioNumero > 0) {
    const precioEnvioEnLinea = clienteNetoActivo ? costoEnvioNumero : ivaEnvio
    const envioItem = {
      sku: 'ENVIO',
      name: 'Costo de Envío',
      product_name: 'Costo de Envío',
      product_id: 'ENVIO',
      quantity: 1,
      price: precioEnvioEnLinea,
      total: precioEnvioEnLinea,
      discount: 0,
      tax_rate: ivaMayor.toString(),
      iva: ivaMayor.toString(),
      is_combo: false,
      components: []
    };
    line_items.push(envioItem);
    console.log('Envío agregado como producto con IVA:', ivaMayor, envioItem);
  }

  // Calcular total bruto (excluyendo envío; con IVA incluido si cliente neto)
  const totalBruto = line_items
    .filter((item: any) => item.sku !== 'ENVIO')
    .reduce((acc: number, item: any) => {
      if (clienteNetoActivo) {
        return acc + getUnitPriceForPdf(item) * Number(item.quantity)
      }
      return acc + (Number(item.price) * Number(item.quantity))
    }, 0);
  documento.totalBruto = totalBruto;

  // Calcular IVA total sumando el IVA individual de cada producto (excluyendo envío)
  const ivaTotal = line_items
    .filter((item: any) => item.sku !== 'ENVIO')
    .reduce((acc: number, item: any) => {
      const valorBrutoItem = Number(item.price) * Number(item.quantity);
      const descuentoItem = Number(item.discount) || 0;
      const valorNetoItem = valorBrutoItem - descuentoItem;
      const taxRate = getTaxRate(item);
      
      // Debug: mostrar información del producto y su IVA
      console.log(`Producto: ${item.name || item.product_name}`);
      console.log(`  - tax_rate: ${item.tax_rate} (tipo: ${typeof item.tax_rate})`);
      console.log(`  - iva: ${item.iva} (tipo: ${typeof item.iva})`);
      console.log(`  - taxRate calculado: ${taxRate}%`);
      console.log(`  - Valor neto: ${valorNetoItem}`);
      
      const ivaItem = Math.round(valorNetoItem * taxRate / 100);
      console.log(`  - IVA calculado: ${ivaItem}`);
      console.log('---');
      
      return acc + ivaItem;
    }, 0);

  // Calcular total neto incluyendo envío
  const subtotal = documento.totalBruto - totalDescuentoItems;
  const subtotalLineasClienteNeto = clienteNetoActivo
    ? line_items
      .filter((item: any) => item.sku !== 'ENVIO')
      .reduce((acc: number, item: any) => acc + getLineSubtotalForPdf(item), 0)
    : 0
  const totalNetoCalculado = clienteNetoActivo
    ? (subtotalLineasClienteNeto + costoEnvioNumero)
    : (subtotal + ivaTotal + costoEnvioNumero + (documento.impConsumo || 0) + (documento.impBebidas || 0) - (documento.reteFuente || 0) - (documento.retIva || 0) - (documento.retIca || 0));

  // Convertir el total a texto en español
  writtenNumber.defaults.lang = 'es';
  const totalEnLetras = writtenNumber(Number(totalNetoCalculado));

  // 4. Crear PDF
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  // Margen y posiciones
  const margenX = 21;
  let y = 20;

  // Logo: ajuste automático de ancho y altura proporcional
  const maxLogoWidth = 140;
  const maxLogoHeight = 60;
  let logoWidth = maxLogoWidth;
  let logoHeight = maxLogoHeight;
  // Crear imagen temporal para obtener dimensiones reales
  const img = new window.Image();
  img.src = logoBase64;
  await new Promise(resolve => {
    img.onload = () => {
      const aspectRatio = img.width / img.height;
      if (img.width > maxLogoWidth) {
        logoWidth = maxLogoWidth;
        logoHeight = logoWidth / aspectRatio;
      } else {
        logoWidth = img.width;
        logoHeight = img.height;
      }
      if (logoHeight > maxLogoHeight) {
        logoHeight = maxLogoHeight;
        logoWidth = logoHeight * aspectRatio;
      }
      resolve(true);
    };
  });
  doc.addImage(logoBase64, 'PNG', margenX, y, logoWidth, logoHeight);
  // Encabezado empresa centrado y más grande
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12); // antes 9, +3pt
  doc.text('MUNDIAL DE MERCANCIAS S.A.S', 300, y + 20, { align: 'center' });
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9); // antes 9, +3pt
  doc.text('900997817-3', 300, y + 38, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.text('CLL 25 A 43 B 199 - TEL. 3014017136', 300, y + 54, { align: 'center' });
  doc.text('MEDELLIN - COLOMBIA', 300, y + 70, { align: 'center' });
  // Recuadro del documento (Cotización o Comprobante)
  doc.setDrawColor(0);
  doc.setLineWidth(1);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255,0,0);
  const tituloDocumento = tipo === 'comprobante' ? 'COMPROBANTE' : 'COTIZACIÓN';
  doc.text(tituloDocumento, 460, y + 15);
  doc.setTextColor(0,0,0);
  doc.roundedRect(430, y + 20, 125, 40, 8, 8);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('No.', 440, y + 45);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255,0,0);
  doc.setFontSize(16); // Más grande para destacar
  doc.text(`${numeroDocumento}`, 470, y + 45);
  doc.setTextColor(0,0,0);
  y += 80;
  
  // Datos del Cliente: recuadro más ancho y una columna principal
  const boxWidth = 540;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('Datos del Cliente', margenX, y + 20);
  doc.setFont('helvetica', 'normal');
  doc.roundedRect(margenX, y + 25, boxWidth, 120, 8, 8);
  // Etiquetas y valores en una columna (izquierda)
  const colX = margenX + 10;
  const row1Y = y + 45;
  const row2Y = y + 65;
  const row3Y = y + 85;
  const row4Y = y + 105;
  const row5Y = y + 125;
  doc.setFont('helvetica', 'bold');
  doc.text('Señores :', colX, row1Y);
  doc.text('NIT :', colX, row2Y);
  doc.text('Dirección :', colX, row3Y);
  doc.text('Ciudad :', colX, row4Y);
  doc.text('Teléfono :', colX, row5Y);
  // Etiquetas y valores en la columna derecha
  const rightColX = margenX + boxWidth - 170;
  doc.text('Fecha de Expedición :  ', rightColX, row1Y);
  if (tipo === 'cotizacion') {
    doc.text('Fecha de Vencimiento : ', rightColX, row2Y);
    doc.text('Vendedor :', rightColX, row3Y);
  } else {
    doc.text('Vendedor :', rightColX, row2Y);
  }
  doc.setFont('helvetica', 'normal');
  doc.text(nombreCliente, colX + 60, row1Y);
  doc.text(nit, colX + 30, row2Y);
  doc.text(direccion, colX + 65, row3Y);
  doc.text(ciudad, colX + 40, row4Y);
  doc.text(telefono, colX + 50, row5Y);
  doc.text(fechaExpedicion, rightColX + 98, row1Y);
  if (tipo === 'cotizacion') {
    doc.text(fechaVencimiento, rightColX + 98, row2Y);
    doc.text(vendedor, rightColX + 60, row3Y);
  } else {
    doc.text(vendedor, rightColX + 60, row2Y);
  }
  y += 170;
  
  const yTableStart = y + 10;
  const colWidths = clienteNetoActivo
    ? [70, 155, 55, 70, 65, 120]
    : [70, 155, 55, 70, 65, 70, 50]; // Código más ancho (70pt), Descripción ajustada (155pt)
  const cantidadColIndex = 2;
  const valorUnitColIndex = 3;
  const descuentoColIndex = 4;
  const subtotalColIndex = 5;
  const impuestosColIndex = 6;
  // Calcular posiciones X de cada columna
  let colPositions = [margenX];
  for (let i = 0; i < colWidths.length; i++) {
    colPositions.push(colPositions[i] + colWidths[i]);
  }
  const cellPadding = 7;
  const codigoExtraPadding = 5;
  // Encabezados de tabla
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('Código', colPositions[0] + cellPadding + codigoExtraPadding, y);
  doc.text('Descripción', colPositions[1] + cellPadding, y);
  doc.text('Cantidad', colPositions[cantidadColIndex + 1] - cellPadding, y, { align: 'right' });
  doc.text('Valor Unit.', colPositions[valorUnitColIndex + 1] - cellPadding, y, { align: 'right' });
  doc.text('Descuento', colPositions[descuentoColIndex + 1] - cellPadding, y, { align: 'right' });
  doc.text('Subtotal', colPositions[subtotalColIndex + 1] - cellPadding, y, { align: 'right' });
  if (!clienteNetoActivo) {
    doc.text('Impuestos', colPositions[impuestosColIndex] + colWidths[impuestosColIndex] / 2, y, { align: 'center' });
  }
  doc.setLineWidth(0.1);
  // Línea superior de la tabla
  doc.line(margenX, y + 5, margenX + colWidths.reduce((a, b) => a + b, 0), y + 5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  let yTable = y + 20;
  
  line_items.forEach((item: any) => {
    // Usar el nombre correcto según el tipo de documento
    const nombreProducto = tipo === 'comprobante' ? (item.product_name || item.name) : (item.name || item.product_name);
    const codigoProducto = tipo === 'comprobante' ? (item.product_id || item.sku) : (item.sku || item.product_id);
    
    // Hacer wrap de texto para código y descripción
    const codigoLines = wrapText(codigoProducto || '', colWidths[0] - cellPadding - codigoExtraPadding - 5, doc);
    const descripcionLines = wrapText(nombreProducto || '', colWidths[1] - cellPadding - 5, doc);
    
    // Calcular altura de la fila basada en el máximo de líneas
    const maxLines = Math.max(codigoLines.length, descripcionLines.length);
    const lineHeight = 12; // Altura por línea
    const rowHeight = maxLines * lineHeight;
    
    // Renderizar líneas de código
    codigoLines.forEach((line, index) => {
      doc.text(line, colPositions[0] + cellPadding + codigoExtraPadding, yTable + (index * lineHeight));
    });
    
    // Renderizar líneas de descripción
    descripcionLines.forEach((line, index) => {
      doc.text(line, colPositions[1] + cellPadding, yTable + (index * lineHeight));
    });
    
    // Resto de columnas alineadas con la primera línea
    doc.text(String(item.quantity), colPositions[cantidadColIndex + 1] - cellPadding, yTable, { align: 'right' });

    const precioUnitarioPdf = getUnitPriceForPdf(item)
    const subtotalLineaPdf = getLineSubtotalForPdf(item)

    doc.text(
      precioUnitarioPdf.toLocaleString('es-CO', { maximumFractionDigits: 2 }),
      colPositions[valorUnitColIndex + 1] - cellPadding,
      yTable,
      { align: 'right' }
    );

    // Descuento (ahora en posición 4)
    const descuento = Number(item.discount) || 0;
    doc.text(descuento.toLocaleString('es-CO', { maximumFractionDigits: 2 }), colPositions[descuentoColIndex + 1] - cellPadding, yTable, { align: 'right' });

    // Valor Total (ahora en posición 5)
    doc.text(
      subtotalLineaPdf.toLocaleString('es-CO', { maximumFractionDigits: 2 }),
      colPositions[subtotalColIndex + 1] - cellPadding,
      yTable,
      { align: 'right' }
    );
    
    // Impuestos
    const valorBrutoItem = Number(item.price) * Number(item.quantity);
    const taxRate = getTaxRate(item);

    let impuestos;
    if (tipo === 'comprobante') {
      impuestos = Math.round((valorBrutoItem - descuento) * taxRate / 100);     
    } else {
      impuestos = Math.round((valorBrutoItem - descuento) * taxRate / 100);
    }

    if (!clienteNetoActivo) {
      const impuestosFormateados = impuestos.toLocaleString('es-CO', { maximumFractionDigits: 2 });
      doc.text(impuestosFormateados, colPositions[impuestosColIndex] + colWidths[impuestosColIndex] / 2, yTable, { align: 'center' });
    }
    
    yTable += rowHeight + 4; // 4pt de espacio entre filas

    // Mostrar componentes si es combo
    if ((item.is_combo === true || item.is_combo === 1) && Array.isArray(item.components) && item.components.length > 0) {
      item.components.forEach((comp: any) => {
        doc.setFontSize(6);
        // Hacer wrap para componentes también
        const compCodigoLines = wrapText('- ' + (comp.sku || ''), colWidths[0] - cellPadding - codigoExtraPadding - 5, doc);
        const compDescripcionLines = wrapText(comp.name || '', colWidths[1] - cellPadding - 5, doc);
        
        const compMaxLines = Math.max(compCodigoLines.length, compDescripcionLines.length);
        const compRowHeight = compMaxLines * 10;
        
        compCodigoLines.forEach((line, index) => {
          doc.text(line, colPositions[0] + 10 + cellPadding + codigoExtraPadding, yTable + (index * 10));
        });
        
        compDescripcionLines.forEach((line, index) => {
          doc.text(line, colPositions[1] + cellPadding, yTable + (index * 10));
        });
        yTable += compRowHeight + 2;
      });
      doc.setFontSize(7);
    }
  });
  // Calcular altura de la tabla y dibujar el borde negro
  const tableHeight = yTable - yTableStart + 5;
  doc.setDrawColor(0,0,0);
  doc.setLineWidth(0.1);
  // Dibujar borde de la tabla
  doc.rect(margenX, yTableStart - 20, colWidths.reduce((a, b) => a + b, 0), tableHeight + 15);
  // Líneas verticales para columnas
  const yColStart = yTableStart - 20;
  const yColEnd = yColStart + tableHeight + 15;
  for (let i = 0; i < colPositions.length; i++) {
    doc.line(colPositions[i], yColStart, colPositions[i], yColEnd);
  }
  // Total
  yTable += 8;
  const leftX = margenX;
  const rightX = margenX + 320;
  const boxHeight = 70;
  
  // Calcular altura total necesaria para la sección inferior
  const altoFila = 10;
  const paddingTot = 15;
  const totBoxHeight = 11 * altoFila + paddingTot; // 11 totales
  const alturaSeccionInferior = Math.max(totBoxHeight, boxHeight) + 40 + 35 + 15; // totales + son + firmas + impreso

  // Aumentamos el margen de seguridad para que salte antes a la siguiente página
  const margenSeguridad = 20; // Margen adicional de seguridad (antes era 0)
  const espacioDisponible = 850 - yTable; // Altura máxima de página - posición actual de la tabla
  const necesitaNuevaPagina = espacioDisponible < (alturaSeccionInferior + margenSeguridad);
  
  let yFooter;
  if (necesitaNuevaPagina) {
    doc.addPage();
    yFooter = 80;
  } else {
    yFooter = Math.max(yTable + 20, 600); // Mínimo 600pt o 20pt después de la tabla
  }
  
  // Observaciones
  const observaciones = tipo === 'comprobante' 
    ? (meta.customer_note || 'No hay comentarios')
    : (getMeta(meta, 'customer_note') || 'No hay comentarios');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.roundedRect(leftX, yFooter, 300, boxHeight, 8, 8);
  doc.text('Observaciones:', leftX + 10, yFooter + 15);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(observaciones, leftX + 10, yFooter + 30, { maxWidth: 280 });
  
  // Totales detallados (altura dinámica)
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);

  const totales = clienteNetoActivo
    ? [
      { label: 'Total Bruto', value: documento.totalBruto || documento.total || 0 },
      { label: '- Descuento', value: totalDescuentoItems },
      { label: '+ Envío', value: costoEnvioNumero || 0 },
      { label: '', value: '' },
      { label: '', value: '' },
      { label: '', value: '' },
      { label: '', value: '' },
      { label: '', value: '' },
      { label: '', value: '' },
      { label: '', value: '' },
      { label: 'Total Neto', value: totalNetoCalculado || 0 },
    ]
    : [
      { label: 'Total Bruto', value: documento.totalBruto || documento.total || 0 },
      { label: '- Descuento', value: totalDescuentoItems },
      { label: 'Subtotal', value: (documento.totalBruto -totalDescuentoItems) + ivaEnvio || 0 },
      //{ label: '+ Envío', value: ivaEnvio || 0 },
      { label: '+ I.V.A.', value: ivaTotal + valorIvaEnvio || 0 },
      { label: '+ Imp.al Consumo', value: documento.impConsumo || 0 },
      { label: '+ Imp.Bebidas Alcoholicas', value: documento.impBebidas || 0 },
      { label: '- Rete.Fuente', value: documento.reteFuente || 0 },
      { label: '', value: '' },
      { label: '', value: '' },
      //{ label: '- Retención I.V.A.', value: documento.retIva || 0 },
     //{ label: '- Retención I.C.A.', value: documento.retIca || 0 },
      { label: 'Total Neto', value: totalNetoCalculado || 0 },
    ];
  let yTot = yFooter + 15;
  
  // Imprimir contenido de totales
  totales.forEach(t => {
    doc.text(t.label, rightX + 10, yTot);
    doc.text((t.value).toLocaleString('es-CO', { maximumFractionDigits: 2 }), rightX + 200, yTot, { align: 'right' });
    yTot += altoFila;
  });
  // Dibujar recuadro de totales después, usando la altura calculada
  doc.roundedRect(rightX, yFooter, 230, totBoxHeight, 8, 8);
  // Son (en letras, por ahora solo el número)
  yFooter += Math.max(totBoxHeight, boxHeight) + 10;
  doc.roundedRect(leftX, yFooter, 550, 30, 8, 8);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('Son:', leftX + 10, yFooter + 20);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  // Mostrar el total en letras con la primera letra en mayúscula y agregando 'pesos m/cte'
  doc.text((totalEnLetras.toUpperCase() + ' PESOS M/CTE'), leftX + 50, yFooter + 20);
  
  // Firma y vendedor SIEMPRE después de 'Son:'
  const alturaFirmas = 35;
  const margenInferior = 40;
  let firmasY = yFooter + 40;
  
  // Si se sale de la hoja, ajusta a la parte inferior pero nunca se superpone
  const maxY = 850 - alturaFirmas - margenInferior;
  if (firmasY > maxY) {
    firmasY = maxY;
  }
  doc.roundedRect(leftX, firmasY, 270, alturaFirmas, 8, 8);
  doc.roundedRect(leftX + 280, firmasY, 270, alturaFirmas, 8, 8);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('Firma o sello del cliente:', leftX + 10, firmasY + 20);
  doc.text('Vendedor:', leftX + 290, firmasY + 20);
  
  // Impreso por
  doc.setFontSize(6);
  doc.text('Impreso  por: www.mdm.com.co', leftX, firmasY + alturaFirmas + 15);
  // Descargar PDF
  const nombreArchivo = tipo === 'comprobante' ? `Comprobante_${numeroDocumento}.pdf` : `Cotizacion_${numeroDocumento}.pdf`;
  doc.save(nombreArchivo);
}

// Función específica para cotizaciones (mantener compatibilidad)
export async function generarPDFCotizacion(cotizacionId: number) {
  return await generarPDFDocumento(cotizacionId, 'cotizacion');
}

// Función específica para comprobantes de pedido
export async function generarPDFComprobante(comprobanteId: number) {
  return await generarPDFDocumento(comprobanteId, 'comprobante');
}